import { LanguageCode } from '@sparted/shared-library/business/types';
import { UseFormWatch } from 'react-hook-form';
import { useMemo } from 'react';

import { CampaignFormValues } from './saveCampaign.hook';

export const useCheckRequiredFields = ({
  watch,
  campaignLanguages,
}: {
  watch: UseFormWatch<CampaignFormValues>;
  campaignLanguages: LanguageCode[];
}) => {
  const { completedLanguages, missingFields } = useMemo(() => {
    return campaignLanguages.reduce(
      (acc, lang) => {
        const fields = ['description', 'name'] as const;

        const missingFields = fields.filter((field) => !watch(`${field}.${lang}`));

        if (missingFields.length > 0) {
          acc.missingFields[lang] = missingFields;
        }

        if (!missingFields.length) {
          acc.completedLanguages.push(lang);
        }

        if (!watch('coverImage')) {
          acc.missingFields[lang] = [...(acc.missingFields[lang] || []), 'coverImage'];
        }

        if (!watch('audiences')) {
          acc.missingFields[lang] = [...(acc.missingFields[lang] || []), 'audiences'];
        }

        const contents = watch('contents') || watch('orderedSessions')?.flat() || [];
        const contentStatuses = contents.filter((content) => content?.statuses?.[lang] !== 'validated');
        const orderedSessionsNotEmpty = watch('orderedSessions')?.filter((session) => session?.length === 0) || [];

        if (contentStatuses.length > 0) {
          acc.missingFields[lang] = [...(acc.missingFields[lang] || []), 'contents'];
        }

        if (orderedSessionsNotEmpty.length > 0 && watch('mode') === 'manual') {
          acc.missingFields[lang] = [...(acc.missingFields[lang] || []), 'orderedSessions'];
        }

        if (contents.length / (watch('nbSessions') || 1) < 1 && watch('mode') === 'random') {
          acc.missingFields[lang] = [...(acc.missingFields[lang] || []), 'orderedSessions'];
        }

        return acc;
      },
      {
        missingFields: {} as { [key in LanguageCode]?: string[] },
        completedLanguages: [] as LanguageCode[],
      },
    );
  }, [campaignLanguages, watch()]);

  return {
    completedLanguages,
    missingFields,
  };
};
